import React, {Fragment} from 'react';
import {
    TextField,
    SimpleShowLayout,
    TabbedForm,
    FormTab,
    BooleanField,
    TextInput,
    LongTextInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    DisabledInput,
    required,
    regex
} from 'admin-on-rest';
import FilterWithSubmit from '../admin/FilterWithSubmit';
import ListTableContainer from '../components/ListTableContainer';
import {DependentInput} from 'aor-dependent-input';
import AdditionalTaxesTable from '../components/AdditionalTaxesTable';
import LocationsWithIdsSelectInput from '../components/LocationsWithIdsSelectInput';
import ValidatedLocationTextInput from '../components/ValidatedLocationTextInput';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import GenerateTSYSTransactionKey from '../components/GenerateTSYSTransactionKey';
import List from '../admin/List';
import Show from '../admin/Show';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {PAYMENT_GATEWAYS as paymentGateways} from '../shared/paymentGateways';
import {US_STATES as usStates} from '../shared/usStates';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/store';

export const fuseLocationIcon = Icon;

const styles = {
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'},
    fieldWide: {flex: '0 1 80%', display: 'block', minWidth: '80%', maxWidth: '100%', overflow: 'hidden'},
    showLabel: {display: 'block', fontSize: 12, color: 'rgba(33, 33, 33, 0.4)', margin: '10px 0 4px 0'},
    showText: {display: 'block', margin: '0 0 18px 0'}
};

// Handle event from Add Additional Tax
const handleAddTax = (e, index, value) => {
};

// Handle event from Remove Additional Tax
const handleRemoveTax = (e) => {
};

const MakeAdditionalTaxesTable = ({record, showButtons, clearList}) => {
    return (
        <AdditionalTaxesTable
            {...record}
            clearList={clearList}
            showAdd={showButtons.add}
            showRemove={showButtons.remove}
            onAddUpdate={handleAddTax}
            onRemoveUpdate={handleRemoveTax}
        />
    )
};

/*
 * View: List
 */

// Create Filter: Filter by + secondary filters
const LocationsFilter = (props) => (
    <FilterWithSubmit {...props} updateOnChange={true} displayButton={false}>
        <ReferenceInput
            label="Select Company"
            source="filter_company"
            reference="Companies"
            dependsOn="filter_by"
            perPage={10000}
            sort={{field: 'name', order: 'ASC'}}
            style={viewStyles.filterInline}
            allowEmpty
        >
            <SelectInput optionText="name" options={{maxHeight: 200}}/>
        </ReferenceInput>
        <ReferenceInput
            label="Select DBA"
            source="filter_dba"
            reference="DBAs"
            dependsOn="filter_by"
            perPage={10000}
            sort={{field: 'name', order: 'ASC'}}
            style={viewStyles.filterInline}
            allowEmpty
        >
            <SelectInput optionText="name" options={{maxHeight: 200}}/>
        </ReferenceInput>
        <ReferenceInput
            label="Select District"
            source="filter_district"
            reference="Districts"
            dependsOn="filter_by"
            perPage={10000}
            sort={{field: 'name', order: 'ASC'}}
            style={viewStyles.filterInline}
            allowEmpty
        >
            <SelectInput optionText="name" options={{maxHeight: 200}}/>
        </ReferenceInput>
        <ReferenceInput
            label="Select Location"
            source="filter_location"
            reference="LocationsForInventory"
            dependsOn="filter_by"
            perPage={10000}
            sort={{field: 'id', order: 'ASC'}}
            allowEmpty
            style={{...viewStyles.filterInline, ...viewStyles.filterHalf}}>
            <VirtualizedSelectInput
                addField={true} optionText="filter_name" optionValue="id"
                options={{
                    className: 'full-width',
                    clearable: false,
                    placeholder: 'Select Location'
                }}
            />
        </ReferenceInput>
        <VirtualizedSelectInput
            addField={true}
            label="Select State"
            choices={usStates}
            source="filter_state"
            optionText="abbreviation"
            optionValue="abbreviation"
            dependsOn="filter_by"
            style={viewStyles.filterInline}
            options={{
                clearable: false,
                labelKey: 'abbreviation',
                className: 'inline',
                placeholder: 'Select State'
            }}
        />
        <SelectInput
            source="filter_by"
            label="Select by:"
            alwaysOn
            choices={[
                {id: 'filter_all_locations', name: 'All Locations'},
                {id: 'filter_location', name: 'Location ID'},
                {id: 'filter_dba', name: 'DBA'},
                {id: 'filter_state', name: 'State'},
                {id: 'filter_company', name: 'Company'},
                {id: 'filter_district', name: 'District'}
            ]}
            style={viewStyles.filterInline}
            defaultValue={'filter_all_locations'}
        />
    </FilterWithSubmit>
);

// Set title for List Locations
const ListTitle =
    <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Locations</span>
;

const ListDescription = () => {
    return (
        <span>
            <p>A <strong>Physical Location</strong> holds your Stores' information and Payment Gateway details so the
                POS System can begin transactions.</p>
            <p>Choose a filter to load Locations. Sort Location results by column. Click a Location's View icon to
                view its details, or Edit to modify the Location. Click <strong>Create</strong> to add a new
                Location.</p>
            <img src="img-diagram-Location-List.png" alt="Diagram of Locations" style={viewStyles.diagram}/>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <p>A <strong>Physical Location</strong> holds your Stores' information and Payment Gateway details so the
                POS System can begin transactions.</p>
            <p>Choose a filter to load Locations. Sort Location results by column. Click a Location's View icon to
                view its details.</p>
            <img src="img-diagram-Location-List.png" alt="Diagram of Locations" style={viewStyles.diagram}/>
        </span>
    );
};

// Set columns and behavior for table
const ListColumns = [
    {source: 'id', label: 'ID', type: 'text'},
    {
        source: 'active',
        label: 'Active',
        component: <BooleanField source="active" label="Active"/>,
        filterable: false,
        width: 90
    },
    {source: 'name', label: 'Name', type: 'text'},
    {source: 'city', label: 'City', type: 'text'},
    {
        source: 'state',
        label: 'State',
        type: 'text',
        width: 90,
        hideOnFilter: 'filter_by',
        hideOnValue: ['filter_state']
    },
    {source: 'dba.name', label: 'DBA', type: 'text', hideOnFilter: 'filter_by', hideOnValue: ['filter_dba']},
    {
        source: 'district.name',
        label: 'District',
        type: 'text',
        hideOnFilter: 'filter_by',
        hideOnValue: ['filter_district']
    },
    {source: 'tax_rate', label: 'Tax Rate', type: 'text'},
    {
        source: 'is_final_auditing',
        label: 'Final Audit',
        component: <BooleanField source="is_final_auditing" label="Final Audit"/>,
        filterable: false,
        width: 90
    },
    {
        source: 'is_open',
        label: 'Open',
        component: <BooleanField source="is_open" label="Open"/>,
        filterable: false,
        width: 90
    },
    {source: '', label: '', component: <ViewButton/>, filterable: false, sortable: false, width: 90},
    {source: '', label: '', component: <EditButton/>, filterable: false, sortable: false, width: 90}
];
const ListColumnsViewOnly = [
    {source: 'id', label: 'ID', type: 'text'},
    {
        source: 'active',
        label: 'Active',
        component: <BooleanField source="active" label="Active"/>,
        filterable: false,
        width: 90
    },
    {source: 'name', label: 'Name', type: 'text'},
    {source: 'city', label: 'City', type: 'text'},
    {
        source: 'state',
        label: 'State',
        type: 'text',
        width: 90,
        hideOnFilter: 'filter_by',
        hideOnValue: ['filter_state']
    },
    {source: 'dba.name', label: 'DBA', type: 'text', hideOnFilter: 'filter_by', hideOnValue: ['filter_dba']},
    {
        source: 'district.name',
        label: 'District',
        type: 'text',
        hideOnFilter: 'filter_by',
        hideOnValue: ['filter_district']
    },
    {source: 'tax_rate', label: 'Tax Rate', type: 'text'},
    {
        source: 'is_final_auditing',
        label: 'Final Audit',
        component: <BooleanField source="is_final_auditing" label="Final Audit"/>,
        filterable: false,
        width: 90
    },
    {
        source: 'is_open',
        label: 'Open',
        component: <BooleanField source="is_open" label="Open"/>,
        filterable: false,
        width: 90
    },
    {source: '', label: '', component: <ViewButton/>, filterable: false, sortable: false, width: 90}
];

/*
 * List Locations table Full Access
 */
export const fuseLocationList = (props) => (
    <List title={ListTitle} desc={<ListDescription/>}
          listType="table"
          filters={<LocationsFilter/>}
          requiresFilter={true}
          allowQueryString={false}
          pagination={null}
          {...props}
    >
        {/* Table Full Access */}
        <ListTableContainer
            {...props}
            columns={ListColumns}
            perPage={20}
            currentSort={{field: 'id', order: 'DESC'}}
        />
    </List>
);

/*
 * List Locations table View Only Access
 */
export const fuseLocationListViewOnly = (props) => (
    <List title={ListTitle} desc={<ListDescriptionViewOnly/>}
          listType="table"
          filters={<LocationsFilter/>}
          requiresFilter={true}
          allowQueryString={false}
          pagination={null}
          {...props}
    >
        {/* Table Full Access */}
        <ListTableContainer
            {...props}
            columns={ListColumnsViewOnly}
            perPage={20}
            currentSort={{field: 'id', order: 'DESC'}}
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show Location
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.name}` : 'Location'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Location details below. To edit this Location, click <strong>Edit</strong> above.</p>
        </span>
    );
};

// Assemble Gateway fields for Show Location
const ShowGatewayFields = ({record}) => {
    return (
        <Fragment>
            <span style={viewStyles.fieldHeader}>Gateway Account</span>
            <hr style={viewStyles.divider}/>
            <span style={styles.showLabel}>Account Method</span>

            {record && record.gateway_account &&
                <Fragment>
                    {record.gateway_account.gateway &&
                        <span style={styles.showText}>{record.gateway_account.gateway}</span>
                    }
                    {record.gateway_account.gateway === 'TSYS' &&
                        <Fragment>
                            <span style={styles.showLabel}>Merchant ID</span>
                            <span style={styles.showText}>{record.gateway_account.merchant_id}</span>
                            <span style={styles.showLabel}>Terminal ID</span>
                            <span style={styles.showText}>{record.gateway_account.terminal_id}</span>
                            <span style={styles.showLabel}>Terminal #</span>
                            <span style={styles.showText}>{record.gateway_account.terminal_nr}</span>
                            <span style={styles.showLabel}>Username/TA#</span>
                            <span style={styles.showText}>{record.gateway_account.username}</span>
                            <span style={styles.showLabel}>Password</span>
                            <span style={styles.showText}>{record.gateway_account.password}</span>
                            <GenerateTSYSTransactionKey resource="Locations" view="show"/>
                        </Fragment>
                    }
                    {record.gateway_account.gateway === 'OOS_DATACAP' &&
                        <Fragment>
                            <span style={styles.showLabel}>Merchant ID</span>
                            <span style={styles.showText}>{record.gateway_account.merchant_id}</span>
                            <span style={styles.showLabel}>Merchant ID Gift Card</span>
                            <span style={styles.showText}>{record.gateway_account.merchant_id_gc}</span>
                            <span style={styles.showLabel}>Operation Mode</span>
                            <span style={styles.showText}>{record.gateway_account.terminal_id}</span>
                        </Fragment>
                    }
                    {record.gateway_account.gateway === 'OOS' &&
                        <Fragment>
                            <span style={styles.showLabel}>Merchant Name</span>
                            <span style={styles.showText}>{record.gateway_account.oos_merchant_name}</span>
                            <span style={styles.showLabel}>Site ID</span>
                            <span style={styles.showText}>{record.gateway_account.oos_merchant_site_id}</span>
                            <span style={styles.showLabel}>Key</span>
                            <span style={styles.showText}>{record.gateway_account.oos_merchant_key}</span>
                        </Fragment>
                    }
                </Fragment>
            }
            <hr style={viewStyles.divider}/>
        </Fragment>
    )
};

/*
 * Show Location Table
 */
export const fuseLocationShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="id" label="Location ID"/>
            <TextField source="name" label="Location Name"/>
            <span style={viewStyles.fieldHeader}>Location Info</span>
            <hr style={viewStyles.divider}/>
            <TextField source="address" label="Address"/>
            <TextField source="city" label="City" style={viewStyles.rowShowField}/>
            <TextField source="state" label="State" style={viewStyles.rowShowField}/>
            <TextField source="zipcode" label="Zip Code" style={viewStyles.rowShowField}/>
            <TextField source="county" label="County"/>
            <TextField source="telephone" label="Phone"/>
            <BooleanField source="city_limits" label="City Limits" style={viewStyles.rowShowField}
                          elStyle={styles.fieldSmall}/>
            <TextField source="lot_type" label="Lot Type" style={viewStyles.rowShowField}/>
            <TextField source="lot_notes" label="Lot Notes"/>
            <TextField source="dba.name" label="DBA"/>
            <TextField source="company.name" label="Company"/>
            <TextField source="location_type.name" label="Location Type"/>
            <TextField source="district.name" label="District"/>
            <TextField source="receipt_message" label="Receipt Message Override"/>
            <BooleanField source="display_receipt_message" label="Display Receipt Message"
                          style={viewStyles.rowShowFieldWide}
                          elStyle={viewStyles.noMargin}/>
            <hr style={viewStyles.divider}/>
            <span style={viewStyles.fieldHeader}>Price & Inventory</span>
            <TextField source="tax_rate" label="Tax" style={viewStyles.rowShowField}/>
            <TextField source="price_type" label="Price Type" style={viewStyles.rowShowField}/>
            <TextField source="commision_rate" label="Commission" style={viewStyles.rowShowField}/>
            <TextField source="price_level.name" label="Price Level"/>
            <ShowGatewayFields/>
            <GenerateTSYSTransactionKey resource="Locations" view="show"/>
            <BooleanField source="test" label="Test Location" style={viewStyles.rowShowField}
                          elStyle={styles.fieldSmall}/>
            <BooleanField source="active" label="Active" style={viewStyles.rowShowField} elStyle={styles.fieldSmall}/>
            <BooleanField source="is_auditing" label="Auditing Status" elStyle={styles.fieldSmall}/>
            <TextField source="close_transfer_warehouse" label="Inventory Transfer Location ID"/>
            <span style={viewStyles.fieldHeader}>Additional Taxes</span>
            {<MakeAdditionalTaxesTable
                showButtons={{'add': false, 'remove': false}}
                clearList={false}
            />}
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create Location
const CreateTitle =
    <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                  className="avatar-title"/>Create a Location</span>
;

const CreateDescription = () => {
    return (
        <span>
    <p>A <strong>Physical Location</strong> holds your Stores' information and Payment Gateway details so the
    POS System can begin transactions.</p>
    <p>Locations require several options to exist before being created. Before creating a Location, you must
    create at least one of each:&nbsp;
        <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Product Type</a>,&nbsp;&nbsp;
        <a href="#/DBAs/create" style={viewStyles.fieldLink}>DBA</a>,&nbsp;&nbsp;
        <a href="#/Companies/create" style={viewStyles.fieldLink}>Company</a>,&nbsp;&nbsp;
        <a href="#/LocationTypes/create" style={viewStyles.fieldLink}>Location Type</a>,&nbsp;&nbsp;
        <a href="#/Districts/create" style={viewStyles.fieldLink}>District</a>, &nbsp;and&nbsp;&nbsp;
        <a href="#/PriceLevels/create" style={viewStyles.fieldLink}>Price Level</a>.</p>
    <p>Enter the required fields in the tabs below.</p>
    </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {
    active: false,
    additional_taxes: [],
    additional_tax_objects: [],
    test: false,
    close_transfer_warehouse_toggle: true,
    close_transfer_warehouse_list: null,
    display_receipt_message: false,
    is_auditing: false,
    is_final_auditing: false,
    is_open: false,
    configuration: '{"quick":[]}',
    receipt_message: ''

};

// Display Close Transfer options based on selection
const CreateLocationTransferToggle = (value) => value === false;

// Display Dependent Gateway inputs based on method
const createGatewayShowTSYSFields = (value) => value === 'TSYS';
const createGatewayShowOOSFields = (value) => value === 'OOS';
const createGatewayShowOOSDatacapFields = (value) => value === 'OOS_DATACAP';

// Validate Required state for Gateway fields if present
const createGatewayFieldValidation = (value, allValues) => {
    let gateway = allValues.gateway_account.gateway;
    if (gateway) {
        if (gateway === 'TSYS' && !value) {
            return 'Required for TSYS Gateway';
        }
        if (gateway === 'OOS' && !value) {
            return 'Required for Out of Scope Gateway';
        }
        if (gateway === 'OOS_DATACAP' && !value) {
            return 'Required for Out of Scope Datacap Gateway';
        }
    }
    return undefined;
};

const validateCreateGatewayFields = [createGatewayFieldValidation];

/*
 * Create Location form
 */
export const fuseLocationCreate = (props) => (
    <Create title={CreateTitle} desc={<CreateDescription/>} {...props}>
        <TabbedForm defaultValue={defaultCreateValues} submitOnEnter={false} redirect="show">

            {/* Create Step: Setup */}
            <FormTab label="Setup" style={viewStyles.row}>

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldDescription}>
                    Enter a unique <strong>Location ID</strong> number and check its availability.
                    <TooltipIcon dataFor="ttId"/>
                    <ReactTooltip id="ttId" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>Location ID</strong> is a unique numerical value used to login to a Location on the
                        POS System. Location IDs must be a number, and must be unique.
                        <br/><br/>
                        The POS System will ask for a Location ID and Register number. Once Location ID is chosen,
                        the POS will upload products and inventory for that Location.
                    </ReactTooltip>
                </span>
                <ValidatedLocationTextInput
                    source="id"
                    fieldType="number"
                    label="Location ID*"
                    buttonLabel="Check Availability"
                    successMessage="This Location ID is available."
                    errorDuplicateMessage="A Location with this ID already exists."
                    errorMaxLengthMessage="A Location ID may be no longer than 5 digits."
                    errorValidateMessage="Need to validate Location ID."
                    errorRequiredMessage="A unique Location ID number is required."
                    fieldResource="Locations"
                    isRequired={true}
                    maxChars={5}
                />

                <ReferenceInput
                    label="Select a DBA*"
                    source="dba.id"
                    reference="DBAs"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>DBA</strong>. (If DBA is not available, please exit and go
                    to <a href="#/DBAs/create" style={viewStyles.fieldLink}>Add DBA</a>).
                    <TooltipIcon dataFor="ttDba"/>
                    <ReactTooltip id="ttDba" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        Locations use <strong>DBAs</strong> to display the correct Product Types on the POS System. A DBA
                        holds the Business Name and Product Type, and is used for Locations to display products of that
                        Product Type on the POS System.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select a Company*"
                    source="company.id"
                    reference="Companies"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Company</strong>. (If Company is not available, please exit and go
                    to <a href="#/Companies/create" style={viewStyles.fieldLink}>Add Company</a>).
                    <TooltipIcon dataFor="ttCompany"/>
                    <ReactTooltip id="ttCompany" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>Company</strong> is used to store the Company name for a Location, and is referred to
                        in Reporting.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select a Location Type*"
                    source="location_type.id"
                    reference="LocationTypes"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Location Type</strong>. (If Location Type is not available, please exit and go
                    to <a href="#/LocationTypes/create" style={viewStyles.fieldLink}>Add Location Type</a>).
                    <TooltipIcon dataFor="ttLocationType"/>
                    <ReactTooltip id="ttLocationType" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>Location Type</strong> describes what type of Location each of your stores is. Your
                        stores may be in different types of locations, such as a brick & mortar, strip mall, tent,
                        stand, warehouse, booth, etc.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select a District*"
                    source="district.id"
                    reference="Districts"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>District</strong>. (If District is not available, please exit and go
                    to <a href="#/Districts/create" style={viewStyles.fieldLink}>Add District</a>).
                    <TooltipIcon dataFor="ttDistrict"/>
                    <ReactTooltip id="ttDistrict" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>District</strong> groups Physical Locations to a geographical location or to a
                        District Manager. Districts are used for Sale and Inventory Reporting to keep track of
                        Districts' progress.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select a Price Level"
                    source="price_level.name"
                    reference="PriceLevels"
                    sort={{field: 'name', order: 'ASC'}}
                    perPage={10000}
                    allowEmpty>
                    <SelectInput optionText="name"/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Price Level</strong>. (If Price Level is not available, please exit and go
                    to <a href="#/PriceLevels/create" style={viewStyles.fieldLink}>Add Price Level</a>).
                    <TooltipIcon dataFor="ttPriceLevel"/>
                    <ReactTooltip id="ttPriceLevel" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>Price Level</strong> is used to label a <em>Price Level Price</em> of a Product.
                        Certain Locations or DBAs could have different pricing for products, so Products can have
                        Price Level Prices based on a certain Price Level.
                        <ul>
                            <li>If a Price Level is selected, a Product with a Price Level Price will display that
                                instead of its default Price.</li>
                            <li>If left blank, the DBA's Price Level will be used to display a Product's Price Level
                                Price instead of its default Price.</li>
                            <li>If DBA's Price Level is also blank, a Product will display its default price.</li>
                        </ul>
                    </ReactTooltip>
                </span>

                <SelectInput source="active" label="Location is Active"
                             validate={[required]}
                             choices={[
                                 {id: true, name: 'Active'},
                                 {id: false, name: 'Inactive'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Select Yes or No.
                    <TooltipIcon dataFor="ttActive"/>
                    <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        If <em>Active</em>, users will be able to login to this Location on the POS System.
                        <br/>
                        If <em>Inactive</em>, users will not be able to login to this Location on the POS System.
                        <br/><br/>
                        A Location might be marked as <em>Inactive</em> in cases where a physical Location such as a
                        pop-up or tent might have closed, or when a physical Location has closed its doors, or in
                        any case where a Location needs to be inaccessible to the POS System.
                    </ReactTooltip>
                </span>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Location Info Tab to proceed.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

            {/* Create Step: Location Info */}
            <FormTab label="Location Info" style={viewStyles.row}>
                <TextInput source="name" label="Location Name" validate={[required]}/>
                <span style={viewStyles.fieldDescription}>
                    The <strong>Name</strong> of this Location.
                </span>

                <LongTextInput source="address" label="Address" validate={[required]}/>

                <TextInput source="city" label="City" validate={[required]} style={viewStyles.rowField}/>

                <VirtualizedSelectInput
                    addField={true}
                    label="State*"
                    choices={usStates}
                    source="state"
                    optionText="abbreviation"
                    optionValue="abbreviation"
                    errorMessage="Required"
                    required={true}
                    style={viewStyles.rowField}
                    options={{
                        clearable: false,
                        labelKey: 'abbreviation',
                        className: 'inline short',
                        placeholder: 'State*',
                        required: true
                    }}
                />

                <TextInput source="zipcode" label="Zip Code"
                           validate={[required, regex(/^\d{5}$/, 'Must be a valid Zip Code')]}
                           style={{...viewStyles.rowField, ...styles.fieldSmall}}/>

                <TextInput source="county" label="County" validate={[required]}
                           style={viewStyles.rowField}/>

                <TextInput source="telephone" label="Telephone" validate={[required]} style={viewStyles.rowField}/>

                <SelectInput source="city_limits" label="City Limits" validate={[required]}
                             choices={[
                                 {id: true, name: 'Inside'},
                                 {id: false, name: 'Outside'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Is this Location inside or outside city limits?
                </span>

                <hr style={viewStyles.break}/>

                <TextInput source="latitude" label="Latitude"
                           validate={[required, regex(/^-?([1-8]?[0-9]\.{1}\d{1,6}$|90\.{1}0{1,6}$)/, 'Must be a valid Latitude')]}/>
                <span style={viewStyles.fieldDescription}>
                    Enter this Location's Latitude value (+/- 90&deg;).
                </span>

                <TextInput source="longitude" label="Longitude"
                           validate={[required, regex(/^-?((([1]?[0-7][0-9]|[1-9]?[0-9])\.{1}\d{1,6}$)|[1]?[1-8][0]\.{1}0{1,6}$)/, 'Must be a valid Longitude')]}/>
                <span style={viewStyles.fieldDescription}>
                    Enter this Location's Longitude value (+/- 180&deg;).
                </span>

                <hr style={viewStyles.break}/>

                <SelectInput source="lot_type" label="Lot Type"
                             validate={[required]}
                             choices={[
                                 {id: 'Own', name: 'Own'},
                                 {id: 'Lease', name: 'Lease'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Is this store Location owned or leased?
                </span>

                <LongTextInput source="lot_notes" label="Lot Notes (Description of Location)"/>
                <span style={viewStyles.fieldDescription}>
                    Enter a description of this Location.
                </span>

                <hr style={viewStyles.break}/>

                <LongTextInput source="receipt_message" label="Receipt Message Override"/>
                <span style={viewStyles.fieldDescription}>
                    If receipts printed at this Location should override specific messaging from the DBA, add it here.
                </span>

                <hr style={viewStyles.break}/>

                <SelectInput source="display_receipt_message" label="Display Receipt Message?"
                             style={viewStyles.rowField}
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Should receipts printed at this Location display specific messaging?
                </span>

                <hr style={viewStyles.break}/>

                <SelectInput source="test" label="Test Location" style={viewStyles.rowField}
                             validate={[required]}
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Is this Location used for testing the POS System?
                </span>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Price & Inventory Tab to proceed.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

            {/* Create Step: Price & Inventory */}
            <FormTab label="Price & Inventory" style={viewStyles.row}>
                <NumberInput source="tax_rate" label="Tax %" step={0.5} validate={[required]}
                             style={viewStyles.rowField}/>

                <SelectInput source="price_type" label="Price Type"
                             style={viewStyles.rowField}
                             choices={[
                                 {id: 'default', name: 'Default'},
                                 {id: 'wholesale', name: 'Wholesale'}
                             ]}/>
                <hr style={viewStyles.break}/>

                <NumberInput source="commision_rate" label="Enter Commission" validate={[required]}/>
                <span style={viewStyles.fieldDescription}>
                    Enter a Commission rate percentage.
                    <TooltipIcon dataFor="ttCommission"/>
                    <ReactTooltip id="ttCommission" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        If stores are operated by employees on commission, input
                        a <strong>Commission Rate</strong> for the Location. A Commission Rate is used in reporting
                        to calculate Location commission.
                    </ReactTooltip>
                </span>
                <hr style={viewStyles.break}/>

                <span style={viewStyles.fieldHeader}>Additional Taxes</span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldDescription}>
                    If one or more <strong>Additional Taxes</strong> are required by law for this Location,
                    add them below. (If a desired Additional Tax is not available, please exit and go
                    to <a href="#/AdditionalTaxes/create" style={viewStyles.fieldLink}>Add Additional Tax</a>).
                </span>
                {<MakeAdditionalTaxesTable
                    showButtons={{'add': true, 'remove': true}}
                    clearList={true}
                />}
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <span style={viewStyles.fieldHeader}>Inventory stays at Location upon Closing?</span>
                <SelectInput source="close_transfer_warehouse_toggle" label=""
                             style={{...viewStyles.rowField, ...styles.fieldSmall}}
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    If Yes, inventory will remain at this Location upon closing. Otherwise, select a different
                    Location to transfer Inventory.
                </span>
                <DependentInput dependsOn="close_transfer_warehouse_toggle" resolve={CreateLocationTransferToggle}>
                    <LocationsWithIdsSelectInput
                        {...props}
                        source="close_transfer_warehouse_list"
                        optionValue="id"
                        label="Select Transfer Location*"
                        errorMessage="Transfer Location is required"
                        isRequired={true}
                        allowEmpty
                    />
                    <span style={viewStyles.fieldDescription}>
                        Select a Location to move inventory to upon Closing.
                    </span>
                </DependentInput>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Gateway Account Tab to proceed.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

            {/* Create Step: Gateway Account */}
            <FormTab label="Gateway Account" style={viewStyles.row}>
                <SelectInput source="gateway_account.gateway" label="Select a Method"
                             style={viewStyles.rowField}
                             defaultValue="cash"
                             choices={paymentGateways}/>
                <DependentInput dependsOn="gateway_account.gateway" resolve={createGatewayShowTSYSFields}>
                    <TextInput source="gateway_account.merchant_id" label="Merchant ID"
                               validate={validateCreateGatewayFields}/>
                    <TextInput source="gateway_account.terminal_id" label="Terminal ID"
                               validate={validateCreateGatewayFields}/>
                    <TextInput source="gateway_account.terminal_nr" label="Terminal Number"/>
                    <TextInput source="gateway_account.username" label="Username"
                               validate={validateCreateGatewayFields}/>
                    <TextInput source="gateway_account.password" label="Password"
                               validate={validateCreateGatewayFields}/>
                    <span style={viewStyles.fieldDescription}>(A Transaction Key will automatically be generated when the
                        Location's POS performs a credit card transaction.)</span>
                </DependentInput>
                <DependentInput dependsOn="gateway_account.gateway" resolve={createGatewayShowOOSFields}>
                    <TextInput source="gateway_account.oos_merchant_name" label="Merchant Name"
                               validate={validateCreateGatewayFields}/>
                    <TextInput source="gateway_account.oos_merchant_site_id" label="Site ID"
                               validate={validateCreateGatewayFields}/>
                    <TextInput source="gateway_account.oos_merchant_key" label="Key"
                               validate={validateCreateGatewayFields}/>
                </DependentInput>
                <DependentInput dependsOn="gateway_account.gateway" resolve={createGatewayShowOOSDatacapFields}>
                    <TextInput source="gateway_account.merchant_id" label="Merchant ID"
                               validate={validateCreateGatewayFields}/>
                    <TextInput source="gateway_account.merchant_id_gc" label="Merchant ID Gift Card"/>
                    <SelectInput source="gateway_account.terminal_id" label="Operation Mode"
                                 validate={validateCreateGatewayFields}
                                 choices={[
                                     {id: 'PROD', name: 'PROD'},
                                     {id: 'CERT', name: 'CERT'}
                                 ]}/>
                </DependentInput>

                <span style={viewStyles.fieldInstructions}>
                    After clicking <strong>Save</strong>, you will be redirected to the Locations List.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>
        </TabbedForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Location
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'Location'}</span>;
};

const EditDescription = () => {
    return (
        <span>
    <p>Modify Location fields in the tabs below. Click <strong>Save</strong> to store this Location's details.</p>
    </span>
    );
};

// Set defaults for Edit form
const defaultEditValues = {
    close_transfer_warehouse_toggle: false,
    close_transfer_warehouse_list: -1
};

// Display Dependent Gateway inputs based on method
const editGatewayShowTSYSFields = (value) => value === 'TSYS';
const editGatewayShowOOSFields = (value) => value === 'OOS';
const editGatewayShowOOSDatacapFields = (value) => value === 'OOS_DATACAP';

// Validate Required state for Gateway fields if present
const editGatewayFieldValidation = (value, allValues) => {
    let gateway = allValues.gateway_account.gateway;
    if (gateway) {
        if (gateway === 'TSYS' && !value) {
            return 'Required for TSYS Gateway';
        }
        if (gateway === 'OOS' && !value) {
            return 'Required for Out of Scope Gateway';
        }
        if (gateway === 'OOS_DATACAP' && !value) {
            return 'Required for Out of Scope Datacap Gateway';
        }
    }
    return undefined;
};

const validateEditGatewayFields = [editGatewayFieldValidation];

/*
 * Edit Location form
 */
export const fuseLocationEdit = (props) => (
    <Edit title={<EditTitle/>} desc={<EditDescription/>} {...props}>
        <TabbedForm defaultValue={defaultEditValues} submitOnEnter={false} redirect="show">

            {/* Edit Step: Setup */}
            <FormTab label="Setup" style={viewStyles.row}>
                <TextField source="id" label="Location ID"/>

                <ReferenceInput
                    label="Select DBA*"
                    source="dba.id"
                    reference="DBAs"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>DBA</strong>. (If not available, please exit and go
                    to <a href="#/DBAs/create" style={viewStyles.fieldLink}>Add DBA</a>).
                    <TooltipIcon dataFor="ttDba"/>
                    <ReactTooltip id="ttDba" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        Locations use <strong>DBAs</strong> to display the correct Product Types on the POS System. A DBA
                        holds the Business Name and Product Type, and is used for Locations to display products of that
                        Product Type on the POS System.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select Company*"
                    source="company.id"
                    reference="Companies"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Company</strong>. (If not available, please exit and go
                    to <a href="#/Companies/create" style={viewStyles.fieldLink}>Add Company</a>).
                    <TooltipIcon dataFor="ttCompany"/>
                    <ReactTooltip id="ttCompany" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>Company</strong> stores the Company name for a Location, and is referred to
                        in Reporting.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select Location Type*"
                    source="location_type.id"
                    reference="LocationTypes"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Location Type</strong>. (If not available, please exit and go
                    to <a href="#/LocationTypes/create" style={viewStyles.fieldLink}>Add Location Type</a>).
                    <TooltipIcon dataFor="ttLocationType"/>
                    <ReactTooltip id="ttLocationType" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>Location Type</strong> describes what type of Location each of your stores is. Your
                        stores may be in different types of locations, such as a brick & mortar, strip mall, tent,
                        stand, warehouse, booth, etc.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select District*"
                    source="district.id"
                    reference="Districts"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    validate={[required]}
                    allowEmpty>
                    <SelectInput optionText="name" validate={[required]} options={{maxHeight: 200}}/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>District</strong>. (If not available, please exit and go
                    to <a href="#/Districts/create" style={viewStyles.fieldLink}>Add District</a>).
                    <TooltipIcon dataFor="ttDistrict"/>
                    <ReactTooltip id="ttDistrict" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>District</strong> groups Physical Locations to a geographical location or to a
                        District Manager. Districts are used for Sale and Inventory Reporting to keep track of
                        Districts' progress.
                    </ReactTooltip>
                </span>

                <ReferenceInput
                    label="Select Price Level"
                    source="price_level.name"
                    reference="PriceLevels"
                    perPage={10000}
                    sort={{field: 'name', order: 'ASC'}}
                    allowEmpty>
                    <SelectInput optionText="name"/>
                </ReferenceInput>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Price Level</strong>. (If not available, please exit and go
                    to <a href="#/PriceLevels/create" style={viewStyles.fieldLink}>Add Price Level</a>).
                    <TooltipIcon dataFor="ttPriceLevel"/>
                    <ReactTooltip id="ttPriceLevel" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        A <strong>Price Level</strong> is used to label a <em>Price Level Price</em> of a Product.
                        Certain Locations or DBAs could have different pricing for products, so Products can have
                        Price Level Prices based on a certain Price Level.
                        <ul>
                            <li>If a Price Level is selected, a Product with a Price Level Price will display that
                                instead of its default Price.</li>
                            <li>If left blank, the DBA's Price Level will be used to display a Product's Price Level
                                Price instead of its default Price.</li>
                            <li>If DBA's Price Level is also blank, a Product will display its default price.</li>
                        </ul>
                    </ReactTooltip>
                </span>

                <SelectInput source="active" label="Location is Active"
                             validate={[required]}
                             choices={[
                                 {id: true, name: 'Active'},
                                 {id: false, name: 'Inactive'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Select Yes or No.
                    <TooltipIcon dataFor="ttActive"/>
                    <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        If <em>Active</em>, users will be able to login to this Location on the POS System.
                        <br/>
                        If <em>Inactive</em>, users will not be able to login to this Location on the POS System.
                        <br/><br/>
                        A Location might be marked as <em>Inactive</em> in cases where a physical Location such as a
                        pop-up or tent might have closed, or when a physical Location has closed its doors, or in
                        any case where a Location needs to be inaccessible to the POS System.
                    </ReactTooltip>
                </span>

                <BooleanField source="is_auditing" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>
                <BooleanField source="is_final_auditing" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>
                <BooleanField source="is_open" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>
                <TextField source="is_open" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Location Info Tab to proceed.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

            {/* Edit Step: Location Info */}
            <FormTab label="Location Info" style={viewStyles.row}>
                <TextInput source="name" label="Location Name" validate={[required]}/>

                <LongTextInput source="address" label="Address" validate={[required]}/>

                <TextInput source="city" label="City" validate={[required]} style={viewStyles.rowField}/>

                <VirtualizedSelectInput
                    addField={true}
                    label="State*"
                    choices={usStates}
                    source="state"
                    optionText="abbreviation"
                    optionValue="abbreviation"
                    errorMessage="Required"
                    required={true}
                    style={viewStyles.rowField}
                    options={{
                        clearable: false,
                        labelKey: 'abbreviation',
                        className: 'inline short',
                        placeholder: 'State*',
                        required: true
                    }}
                />

                <TextInput source="zipcode" label="Zip Code"
                           validate={[required, regex(/^\d{5}$/, 'Must be a valid Zip Code')]}
                           style={{...viewStyles.rowField, ...styles.fieldSmall}}/>

                <TextInput source="county" label="County" validate={[required]}
                           style={viewStyles.rowField}/>

                <TextInput source="telephone" label="Telephone" validate={[required]} style={viewStyles.rowField}/>

                <SelectInput source="city_limits" label="City Limits" validate={[required]}
                             choices={[
                                 {id: true, name: 'Inside'},
                                 {id: false, name: 'Outside'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Is this Location inside or outside city limits?
                </span>

                <hr style={viewStyles.break}/>

                <TextInput source="latitude" label="Latitude"
                           validate={[required, regex(/^-?([1-8]?[0-9]\.{1}\d{1,6}$|90\.{1}0{1,6}$)/, 'Must be a valid Latitude')]}/>

                <TextInput source="longitude" label="Longitude"
                           validate={[required, regex(/^-?((([1]?[0-7][0-9]|[1-9]?[0-9])\.{1}\d{1,6}$)|[1]?[1-8][0]\.{1}0{1,6}$)/, 'Must be a valid Longitude')]}/>

                <hr style={viewStyles.break}/>

                <SelectInput source="lot_type" label="Lot Type" style={viewStyles.rowField}
                             validate={[required]}
                             choices={[
                                 {id: 'Own', name: 'Own'},
                                 {id: 'Lease', name: 'Lease'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Is this store Location owned or leased?
                </span>

                <LongTextInput source="lot_notes" label="Lot Notes (Description of Location)"/>
                <span style={viewStyles.fieldDescription}>
                    Enter a description of this Location.
                </span>

                <hr style={viewStyles.break}/>

                <LongTextInput source="receipt_message" label="Receipt Message Override"/>
                <span style={viewStyles.fieldDescription}>
                    If receipts printed at this Location should override specific messaging from the DBA, add or modify it here.
                </span>

                <hr style={viewStyles.break}/>

                <SelectInput source="display_receipt_message" label="Display Receipt Message?"
                             style={viewStyles.rowField}
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Should receipts printed at this Location display specific messaging?
                </span>

                <hr style={viewStyles.break}/>

                <SelectInput source="test" label="Test Location" style={viewStyles.rowField}
                             validate={[required]}
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Is this Location used for testing the POS System?
                </span>

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Price & Inventory Tab to proceed.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

            {/* Edit Step: Price & Inventory */}
            <FormTab label="Price & Inventory" style={viewStyles.row}>
                <NumberInput source="tax_rate" label="Tax %" step={0.5} validate={[required]}
                             style={viewStyles.rowField}/>

                <SelectInput source="price_type" label="Price Type"
                             style={viewStyles.rowField}
                             choices={[
                                 {id: 'default', name: 'Default'},
                                 {id: 'wholesale', name: 'Wholesale'}
                             ]}/>

                <NumberInput source="commision_rate" label="Commission" validate={[required]}
                             style={viewStyles.rowField}/>
                <span style={viewStyles.fieldDescription}>
                    Enter a Commission rate percentage.
                    <TooltipIcon dataFor="ttCommission"/>
                    <ReactTooltip id="ttCommission" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        If stores are operated by employees on commission, input
                        a <strong>Commission Rate</strong> for the Location. A Commission Rate is used in reporting
                        to calculate Location commission.
                    </ReactTooltip>
                </span>
                <hr style={viewStyles.break}/>

                <span style={viewStyles.fieldHeader}>Additional Taxes</span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldDescription}>
                    If one or more <strong>Additional Taxes</strong> are required by law for this Location,
                    add them below. (If a desired Additional Tax is not available, please exit and go
                    to <a href="#/AdditionalTaxes/create" style={viewStyles.fieldLink}>Add Additional Tax</a>).
                </span>
                {<MakeAdditionalTaxesTable
                    showButtons={{'add': true, 'remove': true}}
                    clearList={false}
                />}
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <span
                    style={viewStyles.fieldHeader}>Current Location to transfer Inventory upon closing Location:</span>
                <TextField source="close_transfer_warehouse" label="Current Location ID"/>
                <span style={viewStyles.fieldDescription}>If no Location is selected to transfer, or the location needs
                    to change, select a new Location below to transfer Inventory.</span>
                <span style={viewStyles.fieldDescription}>If inventory remains at this Location upon closing, select this
                    Location as the Transfer Location.</span>
                <LocationsWithIdsSelectInput
                    {...props}
                    source="close_transfer_warehouse_list"
                    optionValue="id"
                    label="Select new Transfer Location"
                    allowEmpty
                />

                <span style={viewStyles.fieldInstructions}>
                    Upon completing this Tab, click the Gateway Account Tab to proceed.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>

            {/* Edit Step: Gateway Account */}
            <FormTab label="Gateway Account" style={viewStyles.row}>
                <SelectInput source="gateway_account.gateway" label="Select a Method"
                             style={viewStyles.rowField}
                             defaultValue="cash"
                             choices={paymentGateways}/>
                <DependentInput dependsOn="gateway_account.gateway" resolve={editGatewayShowTSYSFields}>
                    <TextInput source="gateway_account.merchant_id" label="Merchant ID"
                               validate={validateEditGatewayFields}/>
                    <TextInput source="gateway_account.terminal_id" label="Terminal ID"
                               validate={validateEditGatewayFields}/>
                    <TextInput source="gateway_account.terminal_nr" label="Terminal Number"/>
                    <TextInput source="gateway_account.username" label="Username" validate={validateEditGatewayFields}/>
                    <TextInput source="gateway_account.password" label="Password" validate={validateEditGatewayFields}/>

                    <DisabledInput source="gateway_account.transaction_key" label="Transaction Key"
                                   style={styles.fieldWide} elStyle={styles.fieldWide}/>
                    <span style={viewStyles.fieldDescription}>(A Transaction Key is automatically generated when the
                        Location's POS performs a credit card transaction.)</span>
                    <GenerateTSYSTransactionKey view="edit"/>
                </DependentInput>
                <DependentInput dependsOn="gateway_account.gateway" resolve={editGatewayShowOOSFields}>
                    <TextInput source="gateway_account.oos_merchant_name" label="Merchant Name"
                               validate={validateEditGatewayFields}/>
                    <TextInput source="gateway_account.oos_merchant_site_id" label="Site ID"
                               validate={validateEditGatewayFields}/>
                    <TextInput source="gateway_account.oos_merchant_key" label="Key"
                               validate={validateEditGatewayFields}/>
                </DependentInput>
                <DependentInput dependsOn="gateway_account.gateway" resolve={editGatewayShowOOSDatacapFields}>
                    <TextInput source="gateway_account.merchant_id" label="Merchant ID"
                               validate={validateEditGatewayFields}/>
                    <TextInput source="gateway_account.merchant_id_gc" label="Merchant ID Gift Card"/>
                    <SelectInput source="gateway_account.terminal_id" label="Operation Mode"
                                 validate={validateCreateGatewayFields}
                                 choices={[
                                     {id: 'PROD', name: 'PROD'},
                                     {id: 'CERT', name: 'CERT'}
                                 ]}/>
                </DependentInput>

                <span style={viewStyles.fieldInstructions}>
                    After clicking <strong>Save</strong>, you will be redirected to the Locations List.
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </FormTab>
        </TabbedForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Location/location.js